import { createContext, useContext, useMemo, useState } from 'react';
import { type ValidationErrors, validationErrorChecker } from 'sonata-react-components';
import { normalizeCountryToCca3 } from 'sonata-static-storage/lib/helpers/countries.helpers';

import { CampaignPlanValidator } from '@/validators/campaignPlanValidator';

import type { MediaAdvertiserUserInterface } from 'sonata-static-storage/lib/schemas/mediaInfo/mediaAdvertiserUser.schema';
import type { CampaignPlan } from 'sonata-static-storage/lib/schemas/multiFlight/campaignPlan.schema';

type Context = {
  _id?: CampaignPlan['_id'];
  completed: CampaignPlan['completed'];
  countries: CampaignPlan['countries'];
  errors?: ValidationErrors;
  iabCategories: CampaignPlan['iabCategories'];
  mediaAdvertiserUser?: CampaignPlan['mediaAdvertiserUser'];
  name: CampaignPlan['name'];
  setCountries: (countries: string[]) => void;
  setIabCategories: (categories: string[]) => void;
  setMediaAdvertiser: (mediaAdvertiserUser: string) => void;
  setName: (name: string) => void;
  userId: CampaignPlan['user'];
};

type Props = {
  advertiser?: MediaAdvertiserUserInterface;
  campaignPlan?: CampaignPlan;
  children: React.ReactElement;
  userCountry?: string;
  userId: CampaignPlan['user'];
};

type State = Omit<Context, 'userId' | 'setName' | 'setCountries' | 'setIabCategories' | 'setMediaAdvertiser'>;

const CampaignPlanContext = createContext<Context | null>(null);

export function CampaignPlanProvider({ advertiser, campaignPlan, children, userCountry, userId }: Readonly<Props>) {
  const defaultCountry = normalizeCountryToCca3(userCountry ?? '');
  const defaultCampaignPlan = {
    completed: false,
    countries: defaultCountry ? [defaultCountry] : [],
    iabCategories: [],
    mediaAdvertiserUser: advertiser?._id ?? '',
    name: 'New campaign plan',
    user: userId
  } as CampaignPlan;

  const cp = campaignPlan || defaultCampaignPlan;
  const [state, setState] = useState<State>({
    ...cp,
    errors: validationErrorChecker(cp, CampaignPlanValidator)
  });

  const setValueAndValidate = (key: keyof State, value: any) => {
    setState(prev => {
      const newState = {
        ...prev,
        [key]: value
      };

      return {
        ...newState,
        errors: validationErrorChecker(newState, CampaignPlanValidator)
      };
    });
  };

  const providerValue = useMemo(
    () => ({
      ...state,
      setCountries: (value: Context['countries']) => setValueAndValidate('countries', value),
      setIabCategories: (value: Context['iabCategories']) => setValueAndValidate('iabCategories', value),
      setMediaAdvertiser: (value: Context['mediaAdvertiserUser']) => setValueAndValidate('mediaAdvertiserUser', value),
      setName: (value: Context['name']) => setValueAndValidate('name', value),
      userId
    }),
    [state]
  );

  return <CampaignPlanContext.Provider value={providerValue}>{children}</CampaignPlanContext.Provider>;
}

export function useCampaignPlan(): Context {
  const context = useContext(CampaignPlanContext);
  if (!context) {
    throw new Error('The hook useCampaignPlan cannot be used outside the CampaignPlanProvider component');
  }

  return context;
}
